import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z, ZodSchema } from "zod";
import { getUser, formatDateTime } from "@/lib/utils";
import { format } from "date-fns";
import FormRow from "./forms/FormRow";
import RecurringClassFields from "./forms/RecurringClassFields";
import CancelClassDialog from "./dialogs/CancelClassDialog";
import DeleteClassDialog from "./dialogs/DeleteClassDialog";
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from "react";

const createFormSchema = (t: (key: string) => string) => {
  return z.object({
    id: z.string(),
    title: z.string().min(2, {
      message: t("classForm.validation.titleRequired"),
    }),
    instructor: z.string(),
    classDateTime: z.string(),
    duration: z.number(),
    capacity: z.number(),
    price: z.number(),
    currency: z.enum(["EUR", "USD", "GBP"]),
    status: z.enum(["Scheduled", "Canceled", "Hidden"]),
    location: z.enum(["Studio", "Online"]),
    recurrent: z.boolean().optional(),
    repeatEvery: z.string().optional(),
    repeatPeriod: z.enum(["Week", "Month", "Year"]).optional(),
    repeatMaxEvents: z.string().optional(),
  })
};

interface ClassFormProps<T> {
  onSubmit: (values: T) => void;
  onCancel?: (id: string) => void;
  onDelete?: (id: string) => void;
  defaultValues?: Partial<T>;
  submitLabel: string;
}

const ClassForm = ({ onSubmit, onCancel, onDelete, defaultValues, submitLabel }: ClassFormProps<z.infer<ReturnType<typeof createFormSchema>>>) => {
  const { t } = useTranslation();
  const adminUser = getUser();
  const formSchema = createFormSchema(t);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      id: "",
      title: "",
      instructor: adminUser.firstName + ' ' + adminUser.lastName,
      classDateTime: defaultValues?.classDateTime || format(new Date(), "yyyy-MM-dd'T'HH:mm"),
      duration: 60,
      capacity: 0,
      price: 0,
      currency: "EUR",
      status: "Scheduled",
      location: "Studio",
      recurrent: false,
      repeatEvery: "1",
      repeatPeriod: "Week",
      repeatMaxEvents: "12",
      ...defaultValues,
    },
  });

  const id = form.watch("id");
  const recurrent = form.watch("recurrent");

  const handleCancel = () => {
    if (onCancel) {
      onCancel(id);
    }
  };

  const handleDelete = () => {
    if (onDelete) {
      onDelete(id);
    }
  };

  const handleSubmit = async (data: any) => {
    console.log("Form submitted with data:", data);
    if (form.formState.errors) {
      console.log("Validation errors:", form.formState.errors);
    }

    // Trigger the original onSubmit if no validation errors
    if (Object.keys(form.formState.errors).length === 0) {
      onSubmit(data);
    }
  };

  useEffect(() => {
    if (id) {
      form.setValue("recurrent", defaultValues?.recurrent);
      form.setValue("repeatEvery", defaultValues?.repeatEvery === null ? undefined : defaultValues?.repeatEvery);
      form.setValue("repeatPeriod", defaultValues?.repeatPeriod === null ? undefined : defaultValues?.repeatPeriod);
      form.setValue("repeatMaxEvents", defaultValues?.repeatMaxEvents === null ? undefined : defaultValues?.repeatMaxEvents);
    }
  }, [id]);

  return (
    <div>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4 max-h-[70vh] overflow-y-auto px-1">
          <input type="hidden" name="id" value={id} />
          <FormField
            control={form.control}
            name="title"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("classForm.title")}</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormRow>
            <FormField
              control={form.control}
              name="classDateTime"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("classForm.datetime")}</FormLabel>
                  <FormControl>
                    <Input type="datetime-local" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="duration"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("classForm.duration")}</FormLabel>
                  <FormControl>
                    <Input type="number" {...field} onBlur={() => { field.onChange(Number(field.value)); }} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </FormRow>

          <FormRow>
            <FormField
              control={form.control}
              name="capacity"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("classForm.capacity")}</FormLabel>
                  <FormControl>
                    <Input type="number" {...field} onBlur={() => { field.onChange(Number(field.value)); }} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="location"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("classForm.location")}</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder={t("classForm.locationOptions.studio")} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="Studio">{t("classForm.locationOptions.studio")}</SelectItem>
                      <SelectItem value="Online">{t("classForm.locationOptions.online")}</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

          </FormRow>
          <FormRow>
            <FormField
              control={form.control}
              name="price"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("classForm.price")}</FormLabel>
                  <FormControl>
                    <Input type="number" {...field} onBlur={() => { field.onChange(Number(field.value)); }} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="currency"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("classForm.currency")}</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder={t("classForm.currencyOptions.eur")} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="EUR">{t("classForm.currencyOptions.eur")}</SelectItem>
                      <SelectItem value="USD">{t("classForm.currencyOptions.usd")}</SelectItem>
                      <SelectItem value="GBP">{t("classForm.currencyOptions.gbp")}</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </FormRow>


          <FormField
            control={form.control}
            name="instructor"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("classForm.trainer")}</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          {
            id === null || id === "" && (
              <>
                <FormField
                  control={form.control}
                  name="recurrent"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                      <div className="space-y-0.5">
                        <FormLabel className="text-base">{t("classForm.isRecurring")}</FormLabel>
                      </div>
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                {recurrent && <RecurringClassFields form={form} />}
              </>
            )
          }
          <div className="flex justify-between space-x-2 pt-4">
            <div className="space-x-5">
              <Button type="submit" className="bg-primary hover:bg-secondary">{submitLabel}</Button>
              {
                id && onCancel && onDelete && (
                  <>
                    <Button type="button" variant="destructive" onClick={() => setShowCancelDialog(true)}>{t('classForm.cancelClass')}</Button>
                    <Button type="button" variant="destructive" onClick={() => setShowDeleteDialog(true)}>{t('classForm.deleteClass')}</Button>
                  </>
                )
              }
            </div>
          </div>
        </form>
      </Form>
      <CancelClassDialog
        open={showCancelDialog}
        onOpenChange={setShowCancelDialog}
        onConfirm={handleCancel}
      />
      <DeleteClassDialog
        open={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        onConfirm={handleDelete}
      />
    </div>
  );
};

export default ClassForm;